// src/components/common/Footer.js
import React from 'react';
import { Link } from 'react-router-dom';
import {
  FiInstagram,
  FiLinkedin,
  FiArrowRight,
  FiGlobe,
  FiMap
} from 'react-icons/fi';

const Footer = () => {
  const currentYear = new Date().getFullYear();
  
  // Enlaces de navegación principal
  const mainLinks = [
    { name: 'Inicio', path: '/' },
    { name: 'Institución', path: '/about' },
    { name: 'Universidad', path: '/university' },
    { name: 'Empleos AV', path: '/empleos' },
    { name: 'Oportunidades', path: '/opportunities' },
    { name: 'Calendario', path: '/calendar' },
    { name: 'Noticias', path: '/news' },
    { name: 'AVlink Espectro', path: '/espectro' },
    { name: 'Sponsors', path: '/sponsors' },
    { name: 'Contacto', path: '/contact' },
    { name: 'Mapa del Sitio', path: '/sitemap', icon: <FiMap className="ml-1 text-primary-500" /> } // Nuevo enlace al sitemap
  ];

  // Enlaces SEO a las áreas del sector AV. Cada uno apunta a una landing
  // de rubro representativa de su área. Sirve como internal linking del hub
  // /empleos a las landings hijas — clave para que Google las indexe bien.
  const empleosLinks = [
    { name: 'Sonidista', path: '/empleos/sonidista' },
    { name: 'Técnico de iluminación', path: '/empleos/tecnico-iluminacion' },
    { name: 'Operador de cámara', path: '/empleos/operador-camara' },
    { name: 'Técnico de broadcast', path: '/empleos/tecnico-broadcast' },
    { name: 'Rigger', path: '/empleos/rigger' },
    { name: 'Técnico AV corporativo', path: '/empleos/tecnico-av-corporativo' },
    { name: 'Ver todos los rubros', path: '/empleos' }
  ];

  // Enlaces SEO B2B al hub de empresas AV. Distribuye PageRank a las 638
  // landings de tipo de empresa × país × ciudad desde toda la web.
  const empresasAvLinks = [
    { name: 'Rental de audio', path: '/empresas-av/rental-audio' },
    { name: 'Rental de iluminación', path: '/empresas-av/rental-iluminacion' },
    { name: 'Rental de video y LED', path: '/empresas-av/rental-video-led' },
    { name: 'Productoras de eventos', path: '/empresas-av/productoras-eventos' },
    { name: 'Estudios de grabación', path: '/empresas-av/estudios-grabacion' },
    { name: 'Integradores AV', path: '/empresas-av/integradores-av' },
    { name: 'Ver todas las empresas', path: '/empresas-av' }
  ];
  
  // Enlaces para recursos (todos redirigen a login)
  const resourceLinks = [
    { name: 'Videoteca', path: '/login' },
    { name: 'Documentos técnicos', path: '/login' },
    { name: 'Cursos disponibles', path: '/login' },
    { name: 'Certificaciones', path: '/login' }
  ];
  
  // Enlaces legales
  const legalLinks = [
    { name: 'Términos y condiciones', path: '/legal/terms' },
    { name: 'Política de privacidad', path: '/legal/privacy' },
    { name: 'Cookies', path: '/legal/cookies' },
    { name: 'Mapa del Sitio', path: '/sitemap' } // Añadido también a enlaces legales
  ];

  return (
    <footer className="bg-dark-900 text-gray-300 py-12 relative">
      {/* Elementos decorativos */}
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-primary-600/30 to-transparent"></div>
      
      <div className="container mx-auto px-6">
        {/* Sección principal del footer */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8 mb-12">
          {/* Columna 1: Sobre Avlink */}
          <div>
            <div className="mb-4">
              <Link to="/" className="text-2xl font-bold text-white">
                <span className="text-primary-500">Avlink</span> Network
              </Link>
            </div>
            <p className="text-gray-400 mb-4">
              La comunidad profesional para profesionales de audio, video e iluminación en Latinoamérica.
            </p>
            <div className="flex space-x-3 mb-4">
              <a
                href="https://www.instagram.com/avlink_network_latam/"
                target="_blank"
                rel="noopener noreferrer"
                className="w-8 h-8 rounded-full bg-dark-800 flex items-center justify-center hover:bg-primary-600 transition-colors duration-300"
                aria-label="Instagram"
              >
                <FiInstagram />
              </a>
              <a
                href="https://www.linkedin.com/company/avlink-network/"
                target="_blank"
                rel="noopener noreferrer"
                className="w-8 h-8 rounded-full bg-dark-800 flex items-center justify-center hover:bg-primary-600 transition-colors duration-300"
                aria-label="LinkedIn"
              >
                <FiLinkedin />
              </a>
            </div>
            <div className="text-sm text-gray-400">
              <Link to="/contact" className="hover:text-primary-400 transition-colors">
                ¿Necesitás contactarnos? Escribinos desde el formulario.
              </Link>
            </div>
          </div>
          
          {/* Columna 2: Navegación */}
          <div>
            <h3 className="text-white font-bold text-lg mb-3">Navegación</h3>
            <ul className="space-y-1">
              {mainLinks.map((link, index) => (
                <li key={index}>
                  <Link 
                    to={link.path} 
                    className="text-gray-400 hover:text-primary-400 transition-colors text-sm flex items-center"
                  >
                    {link.name}
                    {link.icon && link.icon}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          
          {/* Columna 3: Empleos AV — internal linking SEO al hub y a las
              landings principales por rubro. Sumado a propósito acá porque
              el footer aparece en TODA página → distribuye PageRank a las
              landings de empleos. */}
          <div>
            <h3 className="text-white font-bold text-lg mb-3">Empleos AV</h3>
            <ul className="space-y-1">
              {empleosLinks.map((link, index) => (
                <li key={index}>
                  <Link
                    to={link.path}
                    className="text-gray-400 hover:text-primary-400 transition-colors text-sm"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Columna 4: Empresas AV (B2B) — internal linking al hub y
              landings por categoría de empresa. */}
          <div>
            <h3 className="text-white font-bold text-lg mb-3">Empresas AV</h3>
            <ul className="space-y-1">
              {empresasAvLinks.map((link, index) => (
                <li key={index}>
                  <Link
                    to={link.path}
                    className="text-gray-400 hover:text-primary-400 transition-colors text-sm"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Columna 5: Newsletter */}
          <div>
            <h3 className="text-white font-bold text-lg mb-3">Mantente Informado</h3>
            <p className="text-gray-400 mb-3 text-sm">
              Suscríbete para recibir noticias, eventos y oportunidades laborales exclusivas.
            </p>
            <form className="mb-4">
              <div className="flex">
                <input 
                  type="email" 
                  placeholder="Tu correo electrónico" 
                  className="bg-dark-800 text-gray-300 px-4 py-2 rounded-l-lg focus:outline-none focus:ring-1 focus:ring-primary-500 w-full text-sm"
                  required
                />
                <button 
                  type="submit" 
                  className="bg-primary-600 hover:bg-primary-500 text-white px-4 rounded-r-lg transition-colors duration-300"
                  aria-label="Suscribirse"
                >
                  <FiArrowRight />
                </button>
              </div>
              <div className="mt-2 text-xs text-gray-500">
                Al suscribirte, aceptas nuestra política de privacidad.
              </div>
            </form>
          </div>
        </div>

        {/* Parte inferior del footer */}
        <div className="border-t border-dark-800 pt-6">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-3 md:space-y-0">
            {/* Copyright */}
            <div className="text-sm text-gray-500">
              © {currentYear} Avlink Network. Todos los derechos reservados.
            </div>
            
            {/* Enlaces legales */}
            <div className="flex flex-wrap justify-center gap-4">
              {legalLinks.map((link, index) => (
                <Link 
                  key={index} 
                  to={link.path} 
                  className="text-xs text-gray-500 hover:text-gray-400 transition-colors"
                >
                  {link.name}
                </Link>
              ))}
            </div>
            
            {/* Idioma */}
            <div className="flex items-center text-xs text-gray-500">
              <FiGlobe className="mr-1" />
              <select 
                className="bg-transparent border-none focus:outline-none cursor-pointer"
                defaultValue="es"
              >
                <option value="es">Español</option>
                <option value="en">English</option>
                <option value="pt">Português</option>
              </select>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;